import { SERVER_URL } from '../constants.js';
import { useNavigate } from 'react-router-dom';
import { useCallback, useMemo } from 'react';
import { useNotification } from '../contexts/NotificationContext.js';

const getToken = () => sessionStorage.getItem("jwt");

export const useApiClient = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();

  const handleResponse = useCallback(async (response) => {
    if (!response.ok) {
      if (response.status === 401) {
        sessionStorage.clear();
        navigate('/login');
        throw new Error('Unauthorized: Please log in again');
      }
      if (response.status === 429) {
        // Rate limit exceeded
        let errorMessage = 'Too many requests. Please try again later.';
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // If response is not JSON, use default message
        }
        showNotification(errorMessage, 'warning');
        throw new Error(errorMessage);
      }
      const error = await response.text();
      throw new Error(error || 'Request failed');
    }
    return response.json();
  }, [navigate, showNotification]);

  const get = useCallback(async (endpoint) => {
    const token = getToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = token;
    }
    const response = await fetch(`${SERVER_URL}${endpoint}`, {
      headers
    });
    return handleResponse(response);
  }, [handleResponse]);

  const post = useCallback(async (endpoint, body) => {
    const token = getToken();
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = token;
    }
    const response = await fetch(`${SERVER_URL}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
    return handleResponse(response);
  }, [handleResponse]);

  const put = useCallback(async (url, body) => {
    const token = getToken();
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = token;
    }
    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    });
    return response.ok;
  }, []);

  const putR = useCallback(async (endpoint, body) => {
    const token = getToken();
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = token;
    }
    const response = await fetch(`${SERVER_URL}${endpoint}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      if (response.status === 429) {
        let errorMessage = 'Too many requests. Please try again later.';
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // If response is not JSON, use default message
        }
        showNotification(errorMessage, 'warning');
        throw new Error(errorMessage);
      }
      throw new Error(`PUT request failed: ${response.status}`);
    }
    return response.ok;
  }, [showNotification]);

  const deleteMethod = useCallback(async (url) => {
    const token = getToken();
    const headers = {};
    if (token) {
      headers['Authorization'] = token;
    }
    const response = await fetch(url, {
      method: 'DELETE',
      headers
    });
    return response.ok;
  }, []);

  const customDelete = useCallback(async (url) => {
    const token = getToken();
    const endpoint = url.startsWith('http') ? url : `${SERVER_URL}${url}`;
    const headers = {};
    if (token) {
      headers['Authorization'] = token;
    }
    const response = await fetch(endpoint, {
      method: 'DELETE',
      headers
    });

    if (!response.ok) {
      if (response.status === 429) {
        let errorMessage = 'Too many requests. Please try again later.';
        try {
          const errorData = await response.json();
          if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (e) {
          // If response is not JSON, use default message
        }
        showNotification(errorMessage, 'warning');
        throw new Error(errorMessage);
      }
      throw new Error(`DELETE failed: ${response.status}`);
    }
    return response.ok;
  }, [showNotification]);

  return useMemo(() => ({
    get,
    post,
    put,
    putR,
    delete: deleteMethod,
    customDelete
  }), [get, post, put, putR, deleteMethod, customDelete]);
};
